import type { McqQuestion } from "./types";

export type OrderedQuizOption = {
  option: string;
  originalIndex: number;
};

function hashSeed(value: string): number {
  let hash = 2166136261;

  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return hash >>> 0;
}

function seededRandom(seed: number): () => number {
  let state = seed;

  return () => {
    state += 0x6d2b79f5;
    let result = state;
    result = Math.imul(result ^ (result >>> 15), result | 1);
    result ^= result + Math.imul(result ^ (result >>> 7), result | 61);
    return ((result ^ (result >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Returns a stable display order while retaining each option's source index.
 * Stored answers and scoring continue to use the source index, so existing
 * progress remains valid even though answer positions are less predictable.
 */
export function getQuizOptionOrder(
  quizId: string,
  question: Pick<McqQuestion, "id" | "options">
): OrderedQuizOption[] {
  const ordered = question.options.map((option, originalIndex) => ({
    option,
    originalIndex,
  }));
  const random = seededRandom(hashSeed(`${quizId}:${question.id}`));

  for (let index = ordered.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(random() * (index + 1));
    [ordered[index], ordered[swapIndex]] = [ordered[swapIndex], ordered[index]];
  }

  return ordered;
}
